import React, { CSSProperties } from 'react';
import DatePicker, {
  ReactDatePickerProps,
  registerLocale,
  setDefaultLocale,
} from 'react-datepicker';
import styled from 'styled-components';

import vi from 'date-fns/locale/vi';
import en from 'date-fns/locale/en-US';

import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import Header from './components/Header';
import { DESKTOP_WIDTH, MOBILE_WIDTH } from 'app/hooks/useWindowSize';
import { DEFAULT_LANGUAGE } from 'utils/constants';

export interface RangeDatePickerProps extends ReactDatePickerProps<true> {
  responsive?: boolean;
  styleSlots?: {
    container?: CSSProperties;
  };
}

registerLocale('vi', vi);
registerLocale('en', en);
setDefaultLocale(DEFAULT_LANGUAGE);

const RangeDatePicker = (props: RangeDatePickerProps) => {
  const { styleSlots, responsive = true, ...restProps } = props;
  const { locale } = useGlobalFunction();

  return (
    <DatePicker
      selectsRange
      minDate={new Date()}
      inline
      dateFormat="MMMM yyyy"
      locale={locale}
      renderCustomHeader={props => <Header {...props} />}
      calendarContainer={props => (
        <Container
          containerStyle={styleSlots?.container}
          responsive={responsive}
          {...props}
        />
      )}
      weekDayClassName={() => 'weekDayClassCustom'}
      dayClassName={() => 'dayClassCustom'}
      tabIndex={-1}
      renderDayContents={day => <span className="dayContent">{day}</span>}
      {...restProps}
    />
  );
};

export default RangeDatePicker;

const Container = styled.div<{
  containerStyle?: CSSProperties;
  responsive?: boolean;
}>`
  max-height: calc(100vh - 486px);
  min-height: 260px;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow: hidden auto;
  width: 100%;
  display: flex;
  justify-content: center;
  border: none;
  background: transparent;
  .react-datepicker__header {
    background: transparent;
    border: none;
  }
  .weekDayClassCustom {
    margin: 0;
    margin-top: 18px;
    font-weight: 400;
    font-size: 12px;
    font-family: 'Be Vietnam Pro', sans-serif;
    &:not(:first-child) {
      margin-left: 8.5px;
      @media (max-width: ${DESKTOP_WIDTH - 1}px) {
        margin-left: ${props => (props.responsive ? '44px' : '8.5px')};
      }
      @media (max-width: ${MOBILE_WIDTH}px) {
        margin-left: ${props => (props.responsive ? '20px' : '8.5px')};
      }
    }
  }
  .react-datepicker__week {
    &:not(:first-child) {
      margin-top: 18.5px;
    }
  }
  .dayClassCustom {
    &.react-datepicker__day--in-selecting-range {
      .dayContent {
        background: #fef2f1;
        color: #e92127;
        border-radius: 50%;
      }
    }
    font-family: 'Be Vietnam Pro', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #000000;
    &.react-datepicker__day--outside-month {
      color: #c1c1c1;
    }
    margin: 0;
    border-radius: 0;
    width: auto;
    background: transparent;
    &.react-datepicker__day--disabled {
      color: #c1c1c1;
    }
    &:not(:first-child) {
      .dayContent {
        margin-left: 8.5px;
        @media (max-width: ${DESKTOP_WIDTH - 1}px) {
          margin-left: ${props => (props.responsive ? '44px' : '8.5px')};
        }
        @media (max-width: ${MOBILE_WIDTH}px) {
          margin-left: ${props => (props.responsive ? '20px' : '8.5px')};
        }
      }
    }
    &.react-datepicker__day--selected,
    &.react-datepicker__day--selecting-range-start {
      .dayContent {
        background: #e92127;
        color: #ffffff;
        border-radius: 50%;
      }
    }
    &.react-datepicker__day--in-range {
      background: #fef2f1;
      color: #e92127;
      &:first-child {
        background: red;
        border-top-left-radius: 50%;
        border-bottom-left-radius: 50%;
        background: #fef2f1;
      }
      &:last-child {
        background: linear-gradient(to right, #fef2f1 66%, transparent 66%);
        .dayContent {
          border-top-right-radius: 50%;
          border-bottom-right-radius: 50%;
          background: #fef2f1;
        }
      }
      &.react-datepicker__day--range-start {
        background: linear-gradient(to right, transparent 66%, #fef2f1 66%);
        &.react-datepicker__day--range-end {
          background: none;
        }
        &:last-child {
          background: transparent;
        }
        &:first-child {
          background: linear-gradient(to right, transparent 50%, #fef2f1 50%);
        }
        .dayContent {
          background: #e92127;
          color: #ffffff;
          border-radius: 50%;
        }
      }
      &.react-datepicker__day--range-end {
        &:not(.react-datepicker__day--range-start) {
          background: linear-gradient(to left, transparent 33%, #fef2f1 33%);
        }
        &:first-child {
          background: transparent;
        }
        .dayContent {
          background: #e92127;
          color: #ffffff;
          border-radius: 50%;
        }
      }
    }
    .dayContent {
      width: 26px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  @media (max-width: ${DESKTOP_WIDTH - 1}px) {
    max-height: initial;
    .react-datepicker__month-container {
      width: 100%;
    }
  }
  ${props => ({ ...props.containerStyle })}
`;
